/**
 * m3 echo Frontend - Kostenreporting
 *
 * Seite zur Anzeige und Analyse von Kosten über alle Mandanten hinweg.
 */

import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import CodeIcon from '@mui/icons-material/Code';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
// AttachMoneyIcon removed — pricing button moved to KI-Modelle tab
import Chip from '@mui/material/Chip';
// ModelPricingDialog removed — pricing now managed in KI-Modelle tab
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { costApi, CostOverviewResponse, DailyTrend } from '../../services/admin';
import { DEFAULT_CURRENCY, currencySymbol } from '../../utils/currency';

/**
 * Currency and pricing-coverage block the cost API adds to every money-carrying
 * response. The shared CostOverviewResponse type does not model it yet, so it is
 * read defensively here instead of assuming a currency.
 */
interface CostReportMeta {
  currency?: string;
  fx?: {
    source_currency: string;
    target_currency: string;
    rate: number;
    rate_date: string | null;
    is_default_rate: boolean;
  };
  pricing_gaps?: { models: string[]; records: number };
}

// Farben für Diagramme
const COLORS = [
  '#0088FE',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#8884D8',
  '#82CA9D',
  '#FFC658',
  '#FF6B6B',
];

interface CostReportingProps {
  embedded?: boolean;
}

export const CostReporting: React.FC<CostReportingProps> = ({ embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState<CostOverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // Pricing dialog removed — managed in KI-Modelle tab

  // Datumsbereich-Status
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Date range of the loaded overview, so the tenant trend matches it even
  // while the date fields hold an edit that was not applied yet.
  const [loadedRange, setLoadedRange] = useState<{ start?: string; end?: string }>({});

  // Tenant filter of the trend chart: 'all' or a tenant id
  const [trendTenant, setTrendTenant] = useState<string>('all');
  const [tenantTrends, setTenantTrends] = useState<DailyTrend[] | null>(null);
  const [tenantTrendsError, setTenantTrendsError] = useState<string | null>(null);

  useEffect(() => {
    loadCostData();
  }, []);

  const loadCostData = async () => {
    try {
      setLoading(true);
      setError(null);
      const range = { start: startDate || undefined, end: endDate || undefined };
      const response = await costApi.getOverview(range.start, range.end);
      setData(response.data);
      setLoadedRange(range);
    } catch (err: any) {
      console.error('Fehler beim Laden der Kostendaten:', err);
      setError(err.message || 'Fehler beim Laden der Kostendaten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (trendTenant === 'all') {
      setTenantTrends(null);
      setTenantTrendsError(null);
      return;
    }
    let cancelled = false;
    setTenantTrends(null);
    setTenantTrendsError(null);
    costApi
      .getTenantCosts(Number(trendTenant), loadedRange.start, loadedRange.end)
      .then((response) => {
        if (!cancelled) setTenantTrends(response.data.daily_trends);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTenantTrendsError(
            err instanceof Error ? err.message : 'Fehler beim Laden der Mandantenkosten'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [trendTenant, loadedRange]);

  const trendData = trendTenant === 'all' ? data?.daily_trends ?? [] : tenantTrends ?? [];

  const meta = (data ?? {}) as unknown as CostReportMeta;
  const currency = meta.currency ?? DEFAULT_CURRENCY;
  const symbol = currencySymbol(currency);
  const money = (value: number, digits = 2) => `${value.toFixed(digits)} ${symbol}`;

  const handleApplyDateFilter = () => {
    loadCostData();
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    // Neu laden ohne Datumsfilter
    setTimeout(() => {
      loadCostData();
    }, 100);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await costApi.exportCSV(
        startDate || undefined,
        endDate || undefined
      );

      // Download-Link erstellen
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `m3echo_kosten_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Fehler beim Exportieren der Daten:', err);
      setError(err.message || 'Fehler beim Exportieren der Daten');
    } finally {
      setExporting(false);
    }
  };

  const Wrapper = embedded ? React.Fragment : Container;
  const wrapperProps = embedded ? {} : { maxWidth: 'xl' as const, sx: { mt: 4, mb: 4 } };

  if (loading) {
    return (
      <Wrapper {...wrapperProps}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper {...wrapperProps}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Wrapper>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Wrapper {...wrapperProps}>
      {/* Kopfzeile */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {!embedded && (
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Kostenreporting
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Umfassende Kostenanalyse über alle Mandanten
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, ...(embedded ? { ml: 'auto' } : {}) }}>
          <Button
            variant="contained"
            startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            CSV Exportieren
          </Button>
        </Box>
      </Box>

      {/* Datumsbereich-Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Datumsbereich-Filter
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              label="Startdatum"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              type="date"
              label="Enddatum"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={handleApplyDateFilter} fullWidth>
                Anwenden
              </Button>
              <Button variant="outlined" onClick={handleClearDateFilter} fullWidth>
                Zurücksetzen
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Währung und Preisabdeckung — beide kommen aus der API-Antwort */}
      {meta.fx && meta.fx.is_default_rate && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Kein Umrechnungskurs konfiguriert: {meta.fx.source_currency}-Modellpreise werden
          1:1 als {meta.fx.target_currency} ausgewiesen. Für belastbare Zahlen
          REPORTING_FX_USD_TO_EUR setzen.
        </Alert>
      )}
      {meta.fx && !meta.fx.is_default_rate && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Kosten umgerechnet mit {meta.fx.source_currency}/{meta.fx.target_currency} ={' '}
          {meta.fx.rate}
          {meta.fx.rate_date ? ` (Stand ${meta.fx.rate_date})` : ' (kein Stichtag hinterlegt)'}
        </Typography>
      )}
      {meta.pricing_gaps && meta.pricing_gaps.records > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {meta.pricing_gaps.records} Nutzungsdatensätze ohne gültigen Preis zum Nutzungsdatum
          ({meta.pricing_gaps.models.join(', ')}) — die ausgewiesenen Kosten sind eine
          Untergrenze.
        </Alert>
      )}

      {/* Zusammenfassungs-Karten */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocalAtmIcon color="primary" />
                <Typography variant="h6">Gesamtkosten</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {money(data.total_cost)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data.date_range.start && data.date_range.end
                  ? `${data.date_range.start} bis ${data.date_range.end}`
                  : 'Gesamt'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CodeIcon color="success" />
                <Typography variant="h6">Tokens Gesamt</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {data.total_tokens.toLocaleString('de-DE')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Über alle Mandanten
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AssessmentIcon color="warning" />
                <Typography variant="h6">Anfragen Gesamt</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {data.total_requests.toLocaleString('de-DE')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                API-Aufrufe
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUpIcon color="info" />
                <Typography variant="h6">Mandanten</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {data.total_tenants}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aktive Mandanten
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* System Costs Summary */}
      {data.system_costs && data.system_costs.total > 0 && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SettingsIcon color="action" />
            <Typography variant="h6">System-Kosten (Interne Operationen)</Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Gesamtkosten</Typography>
              <Typography variant="h5">{money(data.system_costs.total, 4)}</Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Aufschlüsselung nach Operationstyp</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(data.system_costs.by_operation).map(([opType, cost]) => (
                  <Chip
                    key={opType}
                    label={`${opType}: ${money(cost, 4)}`}
                    size="small"
                    variant="outlined"
                    icon={<SettingsIcon />}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tabs für verschiedene Ansichten */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Kostentrends" />
          <Tab label="Nach Mandant" />
          <Tab label="Nach Modell" />
          <Tab label="Nach Layer" />
        </Tabs>
      </Paper>

      {/* Tab-Panel 0: Kostentrends */}
      {activeTab === 0 && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
              }}
            >
              <Typography variant="h6">Kostentrends im Zeitverlauf</Typography>
              <TextField
                select
                size="small"
                label="Mandant"
                value={trendTenant}
                onChange={(e) => setTrendTenant(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="all">Alle</MenuItem>
                {[...data.tenants]
                  .sort((a, b) => a.display_name.localeCompare(b.display_name, 'de'))
                  .map((tenant) => (
                    <MenuItem key={tenant.id} value={String(tenant.id)}>
                      {tenant.display_name}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
            {tenantTrendsError ? (
              <Alert severity="error">{tenantTrendsError}</Alert>
            ) : trendTenant !== 'all' && tenantTrends === null ? (
              <Box
                sx={{
                  height: isMobile ? 250 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            ) : trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 400}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="cost"
                    stroke="#8884d8"
                    name={`Kosten (${symbol})`}
                    strokeWidth={2}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="tokens"
                    stroke="#82ca9d"
                    name="Tokens"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Keine Daten für den ausgewählten Datumsbereich verfügbar.</Alert>
            )}
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tägliche Kosten und Token-Nutzung
            </Typography>
            {data.daily_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 400}>
                <BarChart data={data.daily_trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="cost"
                    fill="#8884d8"
                    name={`Kosten (${symbol})`}
                  />
                  <Bar
                    yAxisId="right"
                    dataKey="tokens"
                    fill="#82ca9d"
                    name="Tokens"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Keine Daten für den ausgewählten Datumsbereich verfügbar.</Alert>
            )}
          </Paper>
        </>
      )}

      {/* Tab-Panel 1: Nach Mandant */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Kostenaufschlüsselung nach Mandant
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Mandant</TableCell>
                      <TableCell align="right">Kosten</TableCell>
                      <TableCell align="right">Tokens</TableCell>
                      <TableCell align="right">Anfragen</TableCell>
                      <TableCell align="right">Ø Kosten/Anfrage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.tenants.map((tenant) => (
                      <TableRow
                        key={tenant.id}
                        sx={tenant.is_system ? { bgcolor: 'grey.100' } : {}}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {tenant.display_name}
                            {tenant.is_system && (
                              <Chip
                                label="System"
                                size="small"
                                icon={<SettingsIcon />}
                                color="default"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{money(tenant.total_cost, 4)}</TableCell>
                        <TableCell align="right">{tenant.total_tokens.toLocaleString('de-DE')}</TableCell>
                        <TableCell align="right">{tenant.total_requests.toLocaleString('de-DE')}</TableCell>
                        <TableCell align="right">
                          {money(
                            tenant.total_requests > 0
                              ? tenant.total_cost / tenant.total_requests
                              : 0,
                            4
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={12} md={4}>
              {data.tenants.length > 0 && (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <PieChart>
                    <Pie
                      data={data.tenants}
                      dataKey="total_cost"
                      nameKey="display_name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={(entry) => `${entry.display_name}: ${money(entry.total_cost)}`}
                    >
                      {data.tenants.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Tab-Panel 2: Nach Modell */}
      {activeTab === 2 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Token-Nutzung und Kosten nach Modell
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Kosten nach Modell
              </Typography>
              {data.models.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={data.models}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="model_name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total_cost" fill="#8884d8" name={`Kosten (${symbol})`} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info">Keine Modell-Nutzungsdaten verfügbar.</Alert>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Tokens nach Modell
              </Typography>
              {data.models.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={data.models}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="model_name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total_tokens" fill="#82ca9d" name="Tokens" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info">Keine Modell-Nutzungsdaten verfügbar.</Alert>
              )}
            </Grid>
          </Grid>

          {/* Modell-Details-Tabelle */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Detaillierte Modell-Aufschlüsselung
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Modell</TableCell>
                    <TableCell align="right">Gesamtkosten</TableCell>
                    <TableCell align="right">Tokens Gesamt</TableCell>
                    <TableCell align="right">Anfragen</TableCell>
                    <TableCell align="right">Kosten pro 1K Tokens</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.models.map((model) => (
                    <TableRow key={model.model_name}>
                      <TableCell>{model.model_name}</TableCell>
                      <TableCell align="right">{money(model.total_cost, 4)}</TableCell>
                      <TableCell align="right">{model.total_tokens.toLocaleString('de-DE')}</TableCell>
                      <TableCell align="right">{model.total_requests.toLocaleString('de-DE')}</TableCell>
                      <TableCell align="right">
                        {money(
                          model.total_tokens > 0
                            ? (model.total_cost / model.total_tokens) * 1000
                            : 0,
                          4
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      )}
      {/* Tab-Panel 3: Nach Layer */}
      {activeTab === 3 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Kosten und Token-Nutzung nach Layer
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Kosten nach Layer
              </Typography>
              {data.layers && data.layers.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={data.layers}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="layer_name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total_cost" fill="#8884d8" name={`Kosten (${symbol})`} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info">Keine Layer-Nutzungsdaten verfügbar.</Alert>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Tokens nach Layer
              </Typography>
              {data.layers && data.layers.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                  <BarChart data={data.layers}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="layer_name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total_tokens" fill="#82ca9d" name="Tokens" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info">Keine Layer-Nutzungsdaten verfügbar.</Alert>
              )}
            </Grid>
          </Grid>

          {/* Layer-Details-Tabelle */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Detaillierte Layer-Aufschlüsselung
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Layer</TableCell>
                    <TableCell align="right">Gesamtkosten</TableCell>
                    <TableCell align="right">Tokens Gesamt</TableCell>
                    <TableCell align="right">Anfragen</TableCell>
                    <TableCell align="right">Kosten pro 1K Tokens</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.layers && data.layers.map((layer) => (
                    <TableRow key={layer.layer_name}>
                      <TableCell>{layer.layer_name}</TableCell>
                      <TableCell align="right">{money(layer.total_cost, 4)}</TableCell>
                      <TableCell align="right">{layer.total_tokens.toLocaleString('de-DE')}</TableCell>
                      <TableCell align="right">{layer.total_requests.toLocaleString('de-DE')}</TableCell>
                      <TableCell align="right">
                        {money(
                          layer.total_tokens > 0
                            ? (layer.total_cost / layer.total_tokens) * 1000
                            : 0,
                          4
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.layers && data.layers.length > 0 && (
                    <TableRow sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Gesamt</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {money(data.layers.reduce((sum, l) => sum + l.total_cost, 0), 4)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {data.layers.reduce((sum, l) => sum + l.total_tokens, 0).toLocaleString('de-DE')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {data.layers.reduce((sum, l) => sum + l.total_requests, 0).toLocaleString('de-DE')}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {(() => {
                          const totalTokens = data.layers.reduce((sum, l) => sum + l.total_tokens, 0);
                          const totalCost = data.layers.reduce((sum, l) => sum + l.total_cost, 0);
                          return money(
                            totalTokens > 0 ? (totalCost / totalTokens) * 1000 : 0,
                            4
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      )}
    </Wrapper>
  );
};

export default CostReporting;
